import React, { useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const IndividualisedRehabPlanList = ({
  records = [],
  onAdd,
  onEdit,
  onDelete,
}) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ---------- Normalise API payload for the table ----------
  const tableData = useMemo(() => {
    return records.map((plan) => ({
      id: plan.id,
      client_name: plan.patient?.name || "-",
      date: plan.created_at?.split("T")[0] || "-",
      rehab_goal: plan.objectives?.length || 0,
      domain: plan.domain,
      strengths: plan.strengths
        ?.map((s) => s.value)
        .join(", ") || "-",
      weaknesses: plan.weaknesses
        ?.map((w) => w.value)
        .join(", ") || "-",
      objectives_count: plan.objectives?.length || 0,
      created_by: plan.creator?.name || "-",
      updated_by: plan.updater?.name || "-",
    }));
  }, [records]);

  // ---------- Filtering ----------
  const filtered = useMemo(() => {
    return tableData.filter((r) =>
      Object.values(r).some((v) =>
        String(v).toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }, [tableData, filterText]);

  const totalRows = filtered.length;
  const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // ---------- Delete ----------
  const handleDelete = (id) => {
    Swal.fire({
      title: "Confirm Delete",
      text: "Do you want to delete this Individualised Rehab Plan?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) onDelete(id);
    });
  };

  // ---------- PDF Export ----------
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Individualised Rehab Plans", 14, 10);

    const head = [
      "#",
      "Client",
      "Date",
      "Goals",
      "Domain",
      "Strengths",
      "Weaknesses",
      "Objs",
      "Created By",
      "Updated By",
    ];
    const body = filtered.map((r, i) => [
      i + 1,
      r.client_name,
      r.date,
      r.rehab_goal,
      r.domain,
      r.strengths,
      r.weaknesses,
      r.objectives_count,
      r.created_by,
      r.updated_by,
    ]);

    autoTable(doc, { head: [head], body, startY: 20 });
    doc.save("individualised_rehab_plans.pdf");
  };

  // ---------- Custom Pagination ----------
  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) =>
              onChangeRowsPerPage(Number(e.target.value), currentPage)
            }
          >
            {[5, 10, 15, 20].map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(Math.max(1, currentPage - 1), rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-sm border"
            onClick={() =>
              onChangePage(Math.min(totalPages, currentPage + 1), rowCount)
            }
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // ---------- Table columns ----------
  const columns = [
    {
      name: "S.No",
      cell: (_, idx) => idx + 1 + (currentPage - 1) * rowsPerPage,
      width: "70px",
    },
    { name: "Client", selector: (r) => r.client_name, sortable: true },
    { name: "Date", selector: (r) => r.date, sortable: true },
    { name: "Goals", selector: (r) => r.rehab_goal, sortable: true },
    { name: "Domain", selector: (r) => r.domain, sortable: true },
    { name: "Strengths", selector: (r) => r.strengths },
    { name: "Weaknesses", selector: (r) => r.weaknesses },
    {
      name: "Objs",
      selector: (r) => r.objectives_count,
      center: true,
    },
    { name: "Created By", selector: (r) => r.created_by, sortable: true },
    { name: "Updated By", selector: (r) => r.updated_by, sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          <button
            className="btn btn-sm btn-outline-primary"
            title="Edit"
            onClick={() => onEdit(row.id)}
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            title="Delete"
            onClick={() => handleDelete(row.id)}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
      width: "130px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  // ---------- Render ----------
  return (
    <div>
      {/* Header */}
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Export + Search */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={filtered}
                  filename="individualised_rehab_plans.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search Rehab Plans..."
            className="form-control form-control-sm"
            style={{ width: "260px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Record info */}
        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} records
        </h6>

        {/* Add button */}
        <div>
          <button className="btn btn-sm btn-primary" onClick={onAdd}>
            <i className="ri-add-line me-1"></i> Add New
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filtered}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No records found</div>}
        />
      </div>
    </div>
  );
};

export default IndividualisedRehabPlanList;